/**
 * Единственная дверь наружу. Ни один компонент не вызывает fetch напрямую.
 *
 * Три вещи, которые здесь неочевидны и потому вынесены в одно место:
 *
 * 1. `credentials: 'same-origin'` — сессия живёт в httpOnly-cookie, её не видно из JS вовсе.
 *    Значит «выйти» — это POST на сервер, а не удаление токена из localStorage.
 * 2. Конверт ошибки у всех трёх слушателей один: {error:{code,message,details}} (04 §5.1).
 *    Разбираем его здесь и поднимаем типизированное исключение — иначе каждый экран будет
 *    гадать, что пришло: наш конверт, HTML Vite или пустое тело.
 * 3. 401 значит «сессия кончилась», и обработать его обязан кто-то один. Обрабатываем здесь:
 *    подписчики уведомляются, App уводит на /login. Иначе редирект пишется в двадцати местах
 *    и в девятнадцати забывается.
 */

export interface ApiErrorBody {
  readonly error: { readonly code: string; readonly message: string; readonly details?: unknown };
}

export class ApiRequestError extends Error {
  override readonly name = 'ApiRequestError';
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type UnauthorizedListener = () => void;
const unauthorized = new Set<UnauthorizedListener>();

/** Подписка на «сессия кончилась». Возвращает отписку. */
export function onUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorized.add(fn);
  return () => {
    unauthorized.delete(fn);
  };
}

const isApiErrorBody = (v: unknown): v is ApiErrorBody => {
  if (typeof v !== 'object' || v === null || !('error' in v)) return false;
  const e = (v as { error: unknown }).error;
  return typeof e === 'object' && e !== null && 'code' in e && 'message' in e;
};

async function parseBody(res: Response): Promise<unknown> {
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return null;
  try {
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  /**
   * Сырые байты вместо JSON. Взаимоисключимо с `body`: `JSON.stringify` к Blob неприменим,
   * поэтому ветка обязана быть здесь, а не в обёртке над `request`.
   */
  readonly bytes?: Blob;
  readonly signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  // Три взаимоисключающие формы тела: нет тела, JSON, сырые байты. Заголовок и сериализация
  // выбираются вместе — расщепить их значило бы получить `application/json` на байтах.
  const payload: Pick<RequestInit, 'headers' | 'body'> =
    opts.bytes !== undefined
      ? { headers: { 'content-type': opts.bytes.type }, body: opts.bytes }
      : opts.body === undefined
        ? { headers: {} }
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(opts.body) };
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    ...payload,
    ...(opts.signal === undefined ? {} : { signal: opts.signal }),
  };

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (e) {
    // Сеть до loopback падает ровно в одном случае — бэкенд не поднят. Так и скажем.
    throw new ApiRequestError(0, 'network_error', `бэкенд недоступен: ${String(e)}`);
  }

  if (res.status === 401) notifyUnauthorized();

  if (!res.ok) throw errorOf(res.status, await parseBody(res));

  if (res.status === 204) return undefined as T;
  const body = await parseBody(res);
  return body as T;
}

function notifyUnauthorized(): void {
  for (const fn of unauthorized) fn();
}

/** Неуспешный ответ — в типизированное исключение: наш конверт или честное «HTTP N». */
function errorOf(status: number, body: unknown): ApiRequestError {
  if (isApiErrorBody(body)) {
    return new ApiRequestError(status, body.error.code, body.error.message, body.error.details);
  }
  return new ApiRequestError(status, 'unexpected_response', `HTTP ${status}`);
}

export const get = <T>(path: string, signal?: AbortSignal): Promise<T> =>
  request<T>(path, signal === undefined ? {} : { signal });

export const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body }) });

/**
 * Загрузка картинки человеком. Тело — СЫРЫЕ БАЙТЫ с `content-type: image/png|image/jpeg`,
 * без multipart и без base64: ровно так устроен уже работающий `POST /api/v1/media`
 * (маршрут `services/backend/src/listeners/ui.ts:447`, разбор тела — там же, `:179`).
 * Второй формы тела не заводим: FormData потребовала бы двойника в каждом тесте и второго
 * разбора на бэкенде.
 *
 * `content-type` берётся из самого файла, а не подставляется константой: сверять заявленный
 * тип с магическими байтами — работа маршрута (`uploadMedia`), и подменять здесь заявление
 * человека значило бы отнимать у сервера возможность поймать переименованный .txt.
 */
export const postBytes = <T>(path: string, file: Blob, signal?: AbortSignal): Promise<T> =>
  request<T>(path, {
    method: 'POST',
    bytes: file,
    ...(signal === undefined ? {} : { signal }),
  });

/**
 * DELETE — «Удалить файл» полосы «Файл» (docs/release-2026-09-19.md §9.2). Тела нет: всё, что
 * маршруту нужно сверх адреса, едет строкой запроса (`?expected_content_hash=`).
 */
export const del = <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' });

/**
 * PATCH — частичная правка одной строки, где отсутствие поля и `null` значат разное («не
 * трогать» против «сбросить»): вес и включённость источника (`PATCH /api/v1/sources/:code`,
 * миграция 0021). Происхождение и сессию бэкенд проверяет у PATCH так же, как у POST.
 */
export const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body });

/** Сколько тела ушло в сеть. `total: null` — браузер длины не знает (`lengthComputable`). */
export interface UploadProgress {
  readonly loaded: number;
  readonly total: number | null;
}

export interface FormOptions {
  readonly signal?: AbortSignal;
  /** Ход отправки ТЕЛА — байты, ушедшие в сеть; разбор файла сервером сюда не входит. */
  readonly onProgress?: (p: UploadProgress) => void;
  /** Тело ушло целиком: дальше сервер проверяет файл и собирает версию — уже без прогресса. */
  readonly onSent?: () => void;
}

/**
 * Четвёртая форма тела — `multipart/form-data`, ради PDF внизу поста (§9.2): маршрут
 * `POST …/versions/:n/document` принимает файл частью `file` (имя части — это имя, которое
 * увидит читатель канала) и `expected_content_hash` полем формы.
 *
 * ПОЧЕМУ XMLHttpRequest, А НЕ fetch. Файл — до 20 МиБ, и на канале в пару мегабит это десятки
 * секунд. `fetch` хода отправки тела не сообщает вовсе, а экран без хода на таком файле
 * выглядит зависшим, и человек жмёт «Прикрепить» второй раз. `xhr.upload.onprogress` — единственный
 * браузерный способ его увидеть. Дверь при этом та же: конверт ошибки, 401 и отмена разбираются
 * здесь, теми же функциями, что у `request`.
 *
 * `content-type` не ставится руками: границу частей знает только сам браузер, и заголовок без
 * неё сервер не разберёт.
 */
export function postForm<T>(path: string, form: FormData, opts: FormOptions = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const { signal, onProgress, onSent } = opts;
    if (signal?.aborted === true) {
      reject(new ApiRequestError(0, 'aborted', 'загрузка отменена'));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = (): void => {
      xhr.abort();
    };
    const detach = (): void => {
      signal?.removeEventListener('abort', onAbort);
    };
    xhr.open('POST', path);
    if (onProgress !== undefined) {
      xhr.upload.onprogress = (e: ProgressEvent) => {
        onProgress({ loaded: e.loaded, total: e.lengthComputable ? e.total : null });
      };
    }
    if (onSent !== undefined) {
      xhr.upload.onload = () => {
        onSent();
      };
    }
    xhr.onload = () => {
      detach();
      const status = xhr.status;
      const type = xhr.getResponseHeader('content-type') ?? '';
      let body: unknown = null;
      if (type.includes('application/json')) {
        try {
          body = JSON.parse(xhr.responseText) as unknown;
        } catch {
          body = null;
        }
      }
      if (status === 401) notifyUnauthorized();
      if (status < 200 || status >= 300) {
        reject(errorOf(status, body));
        return;
      }
      resolve((status === 204 ? undefined : body) as T);
    };
    // Обрыв до ответа — та же фраза, что у `request`: до loopback сеть падает, когда бэкенд лёг.
    xhr.onerror = () => {
      detach();
      reject(new ApiRequestError(0, 'network_error', 'бэкенд недоступен: соединение оборвалось'));
    };
    xhr.onabort = () => {
      detach();
      reject(new ApiRequestError(0, 'aborted', 'загрузка отменена'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(form);
  });
}
